/**
 * Self-hosted runners of a repository: the inventory, a registration token, and
 * the two lifecycle changes an administrator makes (drain, retire).
 *
 * Every operation needs `runner.admin`; a reader of the repository is refused
 * with 403, and the page says so rather than hiding the section.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

/** The repository a request is scoped to; the same shape every API module uses. */
interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

export type Runner = Schemas['Runner'];
export type RunnerRegistrationToken = Schemas['RunnerRegistrationToken'];

export async function listRunners(scope: RepoScope): Promise<Runner[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/runners', {
      params: { path: repoPath(scope) },
    }),
  );
  return value.data;
}

/** Single use, one hour, returned once; only its hash is stored. */
export function createRunnerRegistrationToken(scope: RepoScope): Promise<RunnerRegistrationToken> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/runners/registration-tokens', {
      params: { path: repoPath(scope) },
    }),
  );
}

/** A draining runner finishes what it holds and takes nothing new. */
export function updateRunner(
  scope: RepoScope,
  runnerId: string,
  draining: boolean,
): Promise<Runner> {
  return expect(
    http.PATCH('/organizations/{org_id}/repositories/{repo_id}/runners/{runner_id}', {
      params: { path: { ...repoPath(scope), runner_id: runnerId } },
      body: { draining },
    }),
  );
}

/** Its credential stops working at once; the record stays, because jobs name it. */
export function deleteRunner(scope: RepoScope, runnerId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/runners/{runner_id}', {
      params: { path: { ...repoPath(scope), runner_id: runnerId } },
    }),
  );
}
